import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/dashboard/header";
import SideMenu from "./student/SideMenu";
import Footer from "../components/dashboard/footer";
import tmdbApi from "../api/tmdbApi";
import { Uploader } from "../utils/upload";
import { ToastContainer, toast } from "react-toastify";

let { lambda, appname } = window.app;

const UPLOAD_BASE_URL  = "https://qih3mm5tl8.execute-api.ap-south-1.amazonaws.com/prod/";
const CLINICAL_LIMIT   = 10;
const HISTOLOGY_LIMIT  = 20;

const IMAGE_EXTENSIONS = new Set(["jpg","jpeg","png","tiff","tif","bmp","webp"]);
const isImageFile = (filename) => {
    if (filename.startsWith(".")) return false;
    if (filename.toLowerCase() === "thumbs.db") return false;
    return IMAGE_EXTENSIONS.has(filename.split(".").pop()?.toLowerCase());
};
const isClinicalFile = (filename) => {
    const n = filename.toLowerCase();
    return n.startsWith("p_") || n.startsWith("p-") || n.startsWith("whatsapp");
};

const determineHistologyPhotoType = (newFiles = [], existingFilesData = []) => {
    const all = [
        ...newFiles.map((f) => f.type?.toLowerCase() || ""),
        ...existingFilesData.map((f) => f.filetype?.toLowerCase() || ""),
    ];
    const hasJPEG = all.some((t) => t.includes("jpeg") || t.includes("jpg") || t.includes("png"));
    const hasTIFF = all.some((t) => t.includes("tiff") || t.includes("tif"));
    if (hasJPEG && hasTIFF) return "TIFF/JPEG";
    if (hasTIFF) return "TIFF";
    if (hasJPEG) return "JPEG";
    return "";
};

// Read width/height/resolution from a JPEG/PNG File object
const getJpegDimensions = (file) =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight, resolution: img.naturalWidth * img.naturalHeight });
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve({ width: 0, height: 0, resolution: 0 }); };
        img.src = url;
    });

const uploadHistologyFile = (file, uploadFilePath, fileNameUnique, bucket) =>
    new Promise((resolve, reject) => {
        let settled = false;
        const uploader = new Uploader({
            file, baseURL: UPLOAD_BASE_URL, chunkSize: 100, threadsQuantity: 3,
            useTransferAcceleration: undefined, bucket: uploadFilePath,
            fileNameUnique, sourceBucket: bucket,
            setIsUploading: (v) => { if (!v && !settled) { settled = true; resolve(); } },
        });
        uploader.onError((err) => { if (!settled) { settled = true; reject(err); } });
        uploader.start();
    });

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
    stepCard: { background:"#fff", borderRadius:"12px", boxShadow:"0 2px 12px rgba(0,0,0,0.07)", marginBottom:"24px", overflow:"hidden" },
    stepHeader: (active) => ({
        background: active ? "linear-gradient(135deg,var(--secondaryColor,#EA7F4A) 0%,#f5a06a 100%)" : "#f8f9fa",
        padding:"14px 20px", display:"flex", alignItems:"center", gap:"12px",
        borderBottom: active ? "none" : "1px solid #eee",
    }),
    stepNum: (active) => ({
        width:"28px", height:"28px", borderRadius:"50%", flexShrink:0,
        background: active ? "rgba(255,255,255,0.3)" : "#e0e0e0",
        color: active ? "#fff" : "#888",
        display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:"13px",
    }),
    stepTitle: (active) => ({ fontWeight:600, fontSize:"14px", color: active ? "#fff" : "#666", margin:0 }),
    stepBody: { padding:"24px" },
    badge: (color, bg) => ({ display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:600, background:bg, color }),
};

const StepHeader = ({ num, title, active, done }) => (
    <div style={S.stepHeader(active)}>
        <div style={S.stepNum(active)}>{done ? <i className="mdi mdi-check" style={{ fontSize:"14px" }} /> : num}</div>
        <span style={S.stepTitle(active)}>{title}</span>
        {done && !active && <span style={{ marginLeft:"auto", fontSize:"12px", color:"#198754", fontWeight:600 }}><i className="mdi mdi-check-circle me-1" />Completed</span>}
    </div>
);

// ── Limit badge shown in table ─────────────────────────────────────────────
const LimitExceededBadge = ({ reason }) => (
    <div style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"3px 10px", borderRadius:"6px", background:"#fde8ea", border:"1px solid #f5c6cb", fontSize:"11px", color:"#842029", fontWeight:600 }}>
        <i className="mdi mdi-cancel" />{reason}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const BulkImageUpload = () => {
    const navigate       = useNavigate();
    const folderInputRef = useRef(null);

    const [folderGroups,        setFolderGroups]        = useState(null);
    const [folderMode,          setFolderMode]          = useState(null);
    const [totalFileCount,      setTotalFileCount]      = useState(0);
    const [detectedFolderNames, setDetectedFolderNames] = useState([]);

    const [previewRows,    setPreviewRows]    = useState([]);
    const [selectedFolders,setSelectedFolders]= useState(new Set()); // folders user chose to upload
    const [uploadStatus,   setUploadStatus]   = useState({});
    const [uploadProgress, setUploadProgress] = useState({});

    const [isScanning,    setIsScanning]    = useState(false);
    const [isUploading,   setIsUploading]   = useState(false);
    const [showPreview,   setShowPreview]   = useState(false);
    const [uploadComplete,setUploadComplete]= useState(false);
    const [uploadSummary, setUploadSummary] = useState(null);

    const currentStep = !folderGroups ? 1 : !showPreview ? 1 : !uploadComplete ? 2 : 3;

    const resetAll = () => {
        setFolderGroups(null); setFolderMode(null); setTotalFileCount(0); setDetectedFolderNames([]);
        setPreviewRows([]); setSelectedFolders(new Set()); setUploadStatus({}); setUploadProgress({});
        setShowPreview(false); setUploadComplete(false); setUploadSummary(null);
    };

    // ── Step 1: Folder selection ─────────────────────────────────────────────
    const handleFolderSelect = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const maxDepth   = Math.max(...files.map((f) => f.webkitRelativePath.split("/").length));
        const isRootMode = maxDepth >= 3;
        const seg        = isRootMode ? 1 : 0;
        console.log(`[BulkUpload] Mode: ${isRootMode ? "root" : "direct"}, maxDepth:${maxDepth}`);

        const groups = {}; let imageCount = 0, skipped = 0;
        files.forEach((file) => {
            const parts = file.webkitRelativePath.split("/");
            if (isRootMode && parts.length < 3) return;
            if (!isRootMode && parts.length < 2) return;
            if (!isImageFile(file.name)) { skipped++; return; }
            const fn = parts[seg];
            if (!groups[fn]) groups[fn] = { histology:[], clinical:[] };
            if (isClinicalFile(file.name)) groups[fn].clinical.push(file);
            else groups[fn].histology.push(file);
            imageCount++;
        });

        const folderNames = Object.keys(groups);
        if (!folderNames.length) { toast.error("No valid case folders found."); e.target.value=""; return; }

        console.log(`[BulkUpload] ${folderNames.length} folders, ${imageCount} images, ${skipped} skipped`);
        setFolderGroups(groups); setFolderMode(isRootMode?"root":"direct");
        setTotalFileCount(imageCount); setDetectedFolderNames(folderNames);
        setShowPreview(false); setPreviewRows([]); setSelectedFolders(new Set());
        setUploadStatus({}); setUploadProgress({}); setUploadComplete(false); setUploadSummary(null);
        e.target.value = "";
    };

    // ── Step 2: Scan & preview ───────────────────────────────────────────────
    const handleScanAndPreview = async () => {
        if (!folderGroups || !Object.keys(folderGroups).length) { toast.error("Please select a folder first."); return; }
        setIsScanning(true);
        try {
            console.log("[BulkUpload] Fetching cases...");
            const response = await tmdbApi.bulkcasesInfo();
            console.log("[BulkUpload] Raw response:", response);

            const rawResult  = response?.result;
            const casesArray = Array.isArray(rawResult) ? rawResult : (rawResult?.questionData || []);
            console.log("[BulkUpload] casesArray length:", casesArray.length, "| sample:", casesArray[0]);
            // Log what file-count fields are available on a case record
            if (casesArray[0]) {
                console.log("[BulkUpload] Case fields available — files:", casesArray[0].files?.length ?? "N/A", "| historyfiles:", casesArray[0].historyfiles?.length ?? "N/A");
            }

            if (!casesArray.length) { toast.error("No cases found in the system."); return; }

            const rows = Object.entries(folderGroups).map(([folderName, { histology, clinical }]) => {
                const lf = folderName.toLowerCase().trim();
                // Word-boundary match: diagnosisid must appear as a complete token in the folder name
                // (not just a substring). Prevents "s2" matching "s254-24 Pemphigus Vulgaris".
                const isWordBoundaryMatch = (str, sub) => {
                    if (!sub || sub.length < 3) return false; // skip very short diagnosisids
                    const idx = str.indexOf(sub);
                    if (idx === -1) return false;
                    const beforeOk = idx === 0 || /[\s\-_]/.test(str[idx - 1]);
                    const afterOk  = idx + sub.length >= str.length || /[\s\-_]/.test(str[idx + sub.length]);
                    return beforeOk && afterOk;
                };

                const exactMatch   = casesArray.find((c) => c.diagnosisid?.toLowerCase().trim() === lf);
                const partialMatch = exactMatch
                    // diagnosisid fully contains the folder name (e.g. diagnosisid is longer)
                    || casesArray.find((c) => {
                        const did = c.diagnosisid?.toLowerCase().trim();
                        return did && did.length >= 3 && did.includes(lf) && lf.length >= 3;
                    })
                    // folder name contains the diagnosisid as a whole word/token
                    || casesArray.find((c) => isWordBoundaryMatch(lf, c.diagnosisid?.toLowerCase().trim()));

                console.log(`[BulkUpload] "${folderName}" → lf:"${lf}" | exact:${!!exactMatch} | partial:${partialMatch?.diagnosisid || "none"}`);


                // Existing counts from bulkcasesInfo projection (if returned)
                const existingHistology = partialMatch?.files?.length            ?? 0;
                const existingClinical  = partialMatch?.historyfiles?.length     ?? 0;
                console.log(`[BulkUpload] "${folderName}" existing — histology:${existingHistology}, clinical:${existingClinical}`);

                // Build limit exceeded reason (if any)
                const histologyTotal = existingHistology + histology.length;
                const clinicalTotal  = existingClinical  + clinical.length;
                const histExceeded   = histologyTotal > HISTOLOGY_LIMIT;
                const clinExceeded   = clinicalTotal  > CLINICAL_LIMIT;
                let limitReason = null;
                if (histExceeded && clinExceeded) {
                    limitReason = `Histology limit exceeded (${histologyTotal}/${HISTOLOGY_LIMIT}) & Clinical limit exceeded (${clinicalTotal}/${CLINICAL_LIMIT})`;
                } else if (histExceeded) {
                    limitReason = `Histology limit exceeded — ${histologyTotal} files, max ${HISTOLOGY_LIMIT}`;
                } else if (clinExceeded) {
                    limitReason = `Clinical limit exceeded — ${clinicalTotal} files, max ${CLINICAL_LIMIT}`;
                }

                return {
                    folderName,
                    caseid        : partialMatch?._id || partialMatch?.caseid || null,
                    diagnosisid   : partialMatch?.diagnosisid  || null,
                    diagnosis     : partialMatch?.diagnosis    || null,
                    histologyFiles: histology,
                    clinicalFiles : clinical,
                    matched       : !!partialMatch,
                    existingHistology,
                    existingClinical,
                    limitExceeded : !!(histExceeded || clinExceeded),
                    limitReason,
                };
            });

            // Default: all matched folders selected, EXCEPT limit-exceeded ones
            const defaultSelected = new Set(rows.filter((r) => r.matched && !r.limitExceeded).map((r) => r.folderName));
            setPreviewRows(rows);
            setSelectedFolders(defaultSelected);
            setShowPreview(true);

            const mc = rows.filter((r) => r.matched).length;
            const limitCount = rows.filter((r) => r.matched && r.limitExceeded).length;
            console.log(`[BulkUpload] ${rows.length} folders, ${mc} matched, ${limitCount} disabled due to limits`);
            toast.success(`${mc} of ${rows.length} folders matched.${limitCount ? ` ${limitCount} folder${limitCount>1?"s":""} disabled — photo limit exceeded.` : ""}`);
        } catch (err) {
            console.error("[BulkUpload] Fetch error:", err);
            console.error("[BulkUpload] Details:", err?.response?.data || err?.message);
            toast.error("Failed to load cases. Please try again.");
        } finally {
            setIsScanning(false);
        }
    };

    // ── Checkbox helpers ─────────────────────────────────────────────────────
    const matchedRows   = previewRows.filter((r) => r.matched);
    const selectedCount = [...selectedFolders].filter((fn) => matchedRows.find((r) => r.folderName === fn)).length;
    const allSelected   = matchedRows.length > 0 && selectedCount === matchedRows.length;
    const someSelected  = selectedCount > 0 && !allSelected;

    const toggleSelectAll = () => {
        if (allSelected) setSelectedFolders(new Set());
        else setSelectedFolders(new Set(matchedRows.map((r) => r.folderName)));
    };
    const toggleFolder = (folderName) => {
        setSelectedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderName)) next.delete(folderName);
            else next.add(folderName);
            return next;
        });
    };

    // ── Step 3: Upload ───────────────────────────────────────────────────────
    const handleUpload = async () => {
        const rowsToUpload = previewRows.filter((r) => r.matched && selectedFolders.has(r.folderName));
        if (!rowsToUpload.length) { toast.error("No folders selected to upload."); return; }

        setIsUploading(true); setUploadComplete(false); setUploadSummary(null);

        const initStatus = {}, initProgress = {};
        previewRows.forEach((r) => {
            const willUpload = r.matched && selectedFolders.has(r.folderName);
            initStatus[r.folderName]   = willUpload ? "pending" : r.matched ? "skipped" : "skipped";
            initProgress[r.folderName] = { done:0, total: r.histologyFiles.length + r.clinicalFiles.length, failed:0 };
        });
        setUploadStatus(initStatus); setUploadProgress(initProgress);

        let summaryDone=0, summaryFailed=0;
        const summarySkipped = previewRows.length - rowsToUpload.length;
        console.log(`[BulkUpload] Upload start — ${rowsToUpload.length} folders selected`);

        for (const row of rowsToUpload) {
            const { folderName, caseid, histologyFiles, clinicalFiles } = row;
            const bucket = window.site?.common?.resourceBucket;

            if (!bucket) { console.error("[BulkUpload] No resource bucket"); toast.error("Config error: resource bucket missing."); setIsUploading(false); return; }
            if (!caseid)  { console.error(`[BulkUpload] No caseid for "${folderName}"`); setUploadStatus((p)=>({...p,[folderName]:"error"})); summaryFailed++; continue; }

            const clinicalToUpload  = clinicalFiles;
            const histologyToUpload = histologyFiles;
            const total = clinicalToUpload.length + histologyToUpload.length;
            setUploadProgress((p)=>({...p,[folderName]:{done:0,total,failed:0}}));
            setUploadStatus((p)=>({...p,[folderName]:"uploading"}));
            let folderErrors = 0;

            // ── Clinical ──────────────────────────────────────────────────
            for (const file of clinicalToUpload) {
                try {
                    const ObjectID = require("bson-objectid");
                    const uniqueId = new ObjectID().toString();
                    const ts = Date.now(); const ext = file.name.split(".").pop();
                    const fileNameUnique  = `${uniqueId}_image_${ts}.${ext}`;
                    const uploadFilePath  = `${appname}/cases/clinicalhistory/${caseid}/${fileNameUnique}`;
                    console.log(`[BulkUpload] Clinical: ${file.name} (${(file.size/1024/1024).toFixed(1)}MB)`);
                    const urlRes = await axios.post(
                        `${lambda}/uploadFiles?appname=${appname}&caseid=${caseid}`,
                        { source_bucket:bucket, sourcepath:uploadFilePath, filename:fileNameUnique },
                        { headers:{ "Content-Type":"application/json" } }
                    );
                    console.log(`[BulkUpload] uploadFiles response for ${file.name}:`, urlRes.data);
                    if (urlRes.data?.result) {
                        await axios.put(urlRes.data.result, file, { headers:{ "Content-Type":file.type, "Cache-Control":"no-cache" } });
                        console.log(`[BulkUpload] Clinical done: ${file.name}`);
                    } else { throw new Error(`No presigned URL for ${file.name}`); }
                    setUploadProgress((p)=>({...p,[folderName]:{...p[folderName],done:p[folderName].done+1}}));
                } catch (err) {
                    folderErrors++;
                    console.error(`[BulkUpload] Clinical failed: ${file.name}`, err?.response?.data || err?.message);
                    setUploadProgress((p)=>({...p,[folderName]:{...p[folderName],done:p[folderName].done+1,failed:p[folderName].failed+1}}));
                }
            }

            // ── Histology ─────────────────────────────────────────────────
            const histologyFilesData = [];
            for (const file of histologyToUpload) {
                try {
                    const ObjectID = require("bson-objectid");
                    const uniqueId  = new ObjectID().toString();
                    const ts  = Date.now(); const ext = file.name.split(".").pop();
                    const fileNameUnique = `${uniqueId}_image_${ts}.${ext}`;
                    const isTiff  = file.type?.toLowerCase().includes("tiff") || file.type?.toLowerCase().includes("tif");
                    const isLarge = file.size > 8*1024*1024;
                    const uploadFilePath = (isTiff || isLarge)
                        ? `${appname}/cases/histology/${caseid}/${uniqueId}/`
                        : `${appname}/cases/histology-jpg/${caseid}/${uniqueId}/`;
                    // Read dimensions for JPEG/PNG before upload (TIFF handled by backend DZI pipeline)
                    const isJpeg = !isTiff && ["image/jpeg","image/jpg","image/png"].includes(file.type?.toLowerCase());
                    let dims = { width: 0, height: 0, resolution: 0 };
                    if (isJpeg) {
                        try { dims = await getJpegDimensions(file); } catch (e) { console.warn(`[BulkUpload] Could not read dims for ${file.name}:`, e); }
                    }
                    console.log(`[BulkUpload] Histology: ${file.name} (${(file.size/1024/1024).toFixed(1)}MB, isTiff:${isTiff}, isLarge:${isLarge}, w:${dims.width}, h:${dims.height})`);
                    await uploadHistologyFile(file, uploadFilePath, fileNameUnique, bucket);
                    console.log(`[BulkUpload] Histology done: ${file.name}`);

                    const newFile = {
                        fileCreated: new Date(), filename: fileNameUnique, filetype: file.type,
                        sourcepath: uploadFilePath + fileNameUnique, fileid: uniqueId,
                        originalFilename: file.name, uploadStatus: "COMPLETED",
                        fileSize: file.size,
                        width: dims.width, height: dims.height, resolution: dims.resolution,
                    };
                    // thumbnailUrl is a deterministic path the backend also uses — set it now so the UI shows the thumbnail immediately
                    if (isJpeg && !isLarge) {
                        newFile.thumbnailUrl = `/${appname}/cases/dzi-thumbnail/${caseid}/${uniqueId}.jpg`;
                        console.log(`[BulkUpload] thumbnailUrl set: ${newFile.thumbnailUrl}`);
                    }
                    if (isTiff || isLarge) { newFile.allow=true; newFile.allowDziCreation=true; newFile.uploadStatus="INPROGRESS"; }
                    if (!isLarge && isJpeg) newFile.dzistatus="COMPLETED";
                    histologyFilesData.push(newFile);
                    setUploadProgress((p)=>({...p,[folderName]:{...p[folderName],done:p[folderName].done+1}}));
                } catch (err) {
                    folderErrors++;
                    console.error(`[BulkUpload] Histology failed: ${file.name}`, err?.message);
                    setUploadProgress((p)=>({...p,[folderName]:{...p[folderName],done:p[folderName].done+1,failed:p[folderName].failed+1}}));
                }
            }

            // Save histology refs + histologyPhotoType
            if (histologyFilesData.length > 0) {
                try {
                    const photoType = determineHistologyPhotoType(histologyToUpload, []);
                    console.log(`[BulkUpload] "${folderName}" histologyPhotoType:${photoType}, saving ${histologyFilesData.length} file refs`);
                    await axios({
                        method:"POST",
                        url:`${lambda}/updateLearningCase?appname=${appname}&caseid=${caseid}`,
                        data:{ files:histologyFilesData, caseid, ...(photoType ? { histologyPhotoType:photoType } : {}) },
                    });
                    console.log(`[BulkUpload] updateLearningCase done for "${folderName}"`);
                } catch (err) {
                    console.error(`[BulkUpload] updateLearningCase failed for "${folderName}":`, err?.message);
                    folderErrors += histologyFilesData.length;
                }
            }

            const uploadedTotal = clinicalToUpload.length + histologyToUpload.length;
            if      (folderErrors === 0)             { setUploadStatus((p)=>({...p,[folderName]:"done"}));    summaryDone++; }
            else if (folderErrors < uploadedTotal)   { setUploadStatus((p)=>({...p,[folderName]:"partial"})); summaryDone++; }
            else                                     { setUploadStatus((p)=>({...p,[folderName]:"error"}));   summaryFailed++; }
        }

        setIsUploading(false); setUploadComplete(true);
        setUploadSummary({ done:summaryDone, failed:summaryFailed, skipped:summarySkipped });
        console.log(`[BulkUpload] Complete — done:${summaryDone}, failed:${summaryFailed}, skipped:${summarySkipped}`);
        summaryFailed === 0
            ? toast.success(`All ${summaryDone} folder${summaryDone!==1?"s":""} uploaded successfully!`)
            : toast.error(`Upload done — ${summaryFailed} folder${summaryFailed!==1?"s":""} failed.`);
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const totalFolders   = folderGroups ? Object.keys(folderGroups).length : 0;
    const showStatusCol  = Object.keys(uploadStatus).length > 0;
    const limitWarnCount = previewRows.filter((r) => r.matched && r.limitExceeded).length;

    const statusConfig = {
        pending  : { color:"#6c757d", bg:"#f0f0f0", icon:"mdi-clock-outline",  label:"Pending"  },
        uploading: { color:"#0d6efd", bg:"#e8f0fe", icon:"mdi-upload",         label:null       },
        done     : { color:"#198754", bg:"#d1f0e0", icon:"mdi-check-circle",   label:"Done"     },
        partial  : { color:"#e07b00", bg:"#fff0dc", icon:"mdi-alert-circle",   label:null       },
        error    : { color:"#dc3545", bg:"#fde8ea", icon:"mdi-close-circle",   label:"Failed"   },
        skipped  : { color:"#888",    bg:"#f5f5f5", icon:"mdi-minus-circle",   label:"Skipped"  },
    };

    const StatusBadge = ({ folderName }) => {
        const s    = uploadStatus[folderName];
        const prog = uploadProgress[folderName];
        const cfg  = statusConfig[s];
        if (!cfg) return null;
        return (
            <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"3px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:600, background:cfg.bg, color:cfg.color }}>
                {s === "uploading" ? <><span className="spinner-border spinner-border-sm" style={{width:"10px",height:"10px"}} />{prog?.done??0}/{prog?.total??0}</>
                  : s === "partial" ? <><i className={`mdi ${cfg.icon}`} />{prog?.failed??0} file{prog?.failed!==1?"s":""} failed</>
                  : <><i className={`mdi ${cfg.icon}`} />{cfg.label}</>}
            </span>
        );
    };

    // ────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <SideMenu />
                <div className="main-content" id="main-content">
                    <div className="page-content" id="page-content">
                        <div className="container-fluid" id="container-fluid">

                            {/* Page header */}
                            <div className="row mb-4 breadcrumb">
                                <div className="col-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-1 card-title">BULK IMAGE UPLOAD</h4>
                                            <p style={{ fontSize:"13px", color:"#999", margin:0 }}>Upload images to multiple cases at once from a local folder</p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            {uploadComplete && (
                                                <button className="btn btn-primarys create-quiz-btn" onClick={resetAll}>
                                                    <i className="mdi mdi-refresh me-1" />Upload Another Batch
                                                </button>
                                            )}
                                            <a onClick={() => navigate("/cases")} className="btn btn-primarys create-quiz-btn" style={{ cursor:"pointer" }}>Back</a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Step bar */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div style={{ display:"flex", alignItems:"center", background:"#fff", borderRadius:"10px", padding:"14px 24px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                                        {[{n:1,label:"Select Folder"},{n:2,label:"Preview Matches"},{n:3,label:"Upload Complete"}].map((step,i) => (
                                            <React.Fragment key={step.n}>
                                                <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                                                    <div style={{ width:"30px", height:"30px", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:"13px", transition:"all 0.3s", background: currentStep>step.n?"#198754":currentStep===step.n?"var(--secondaryColor,#EA7F4A)":"#e8e8e8", color: currentStep>=step.n?"#fff":"#aaa" }}>
                                                        {currentStep>step.n ? <i className="mdi mdi-check" /> : step.n}
                                                    </div>
                                                    <span style={{ fontSize:"12px", fontWeight:600, color: currentStep>=step.n?"#333":"#bbb" }}>{step.label}</span>
                                                </div>
                                                {i < 2 && <div style={{ flex:1, height:"2px", margin:"0 12px", transition:"all 0.3s", background: currentStep>step.n?"#198754":"#e8e8e8" }} />}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* ── STEP 1 ────────────────────────────────── */}
                            <div style={S.stepCard}>
                                <StepHeader num={1} title="Select Folder" active={currentStep===1} done={currentStep>1} />
                                <div style={S.stepBody}>
                                    <p style={{ fontSize:"13px", color:"#777", marginBottom:"20px", lineHeight:"1.6" }}>
                                        Select your <strong>root folder</strong> (containing case-number subfolders) or select case-number folders directly.
                                        Files named <code style={{ background:"#f5f5f5", padding:"1px 6px", borderRadius:"4px" }}>p_*</code>,&nbsp;
                                        <code style={{ background:"#f5f5f5", padding:"1px 6px", borderRadius:"4px" }}>p-*</code> or&nbsp;
                                        <code style={{ background:"#f5f5f5", padding:"1px 6px", borderRadius:"4px" }}>WhatsApp*</code> → <strong>Clinical History Photos</strong> (max {CLINICAL_LIMIT}).
                                        All others → <strong>Histology Photos</strong> (max {HISTOLOGY_LIMIT}).
                                    </p>
                                    <div style={{ display:"flex", gap:"12px", alignItems:"center", flexWrap:"wrap" }}>
                                        <button className="btn btn-primarys create-quiz-btn" onClick={() => folderInputRef.current.click()} disabled={isUploading}>
                                            <i className="mdi mdi-folder-open-outline me-2" />{totalFolders>0?"Change Folder":"Select Folder"}
                                        </button>
                                        {totalFolders>0 && !uploadComplete && (
                                            <button className="btn btn-primarys create-quiz-btn" onClick={handleScanAndPreview} disabled={isScanning||isUploading}>
                                                {isScanning ? <><span className="spinner-border spinner-border-sm me-2" />Scanning...</> : <><i className="mdi mdi-magnify me-2" />Preview Matches</>}
                                            </button>
                                        )}
                                    </div>

                                    {totalFolders>0 && (
                                        <div style={{ marginTop:"20px", borderRadius:"10px", border:"1px solid #e8f4fd", background:"linear-gradient(135deg,#f0f8ff 0%,#fafcff 100%)", padding:"18px 20px" }}>
                                            <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px" }}>
                                                <div style={{ width:"36px", height:"36px", borderRadius:"8px", background:"var(--secondaryColor,#EA7F4A)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                                                    <i className="mdi mdi-folder-multiple" style={{ color:"#fff", fontSize:"18px" }} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight:700, fontSize:"14px", color:"#333" }}>Folder Scan Result</div>
                                                    <div style={{ fontSize:"12px", color:"#888" }}>{folderMode==="root"?"Root folder mode — reading subfolders":"Direct case folder mode"}</div>
                                                </div>
                                                {folderMode==="root" && <span style={{ marginLeft:"auto", ...S.badge("#856404","#fff3cd") }}><i className="mdi mdi-folder-tree" />Root Mode</span>}
                                            </div>
                                            <div style={{ display:"flex", gap:"12px", flexWrap:"wrap", marginBottom:"14px" }}>
                                                {[
                                                    { label:"Case Folders", value:totalFolders, color:"var(--secondaryColor,#EA7F4A)" },
                                                    { label:"Images Found", value:totalFileCount, color:"#0d6efd" },
                                                    { label:"Clinical Files", value:Object.values(folderGroups||{}).reduce((s,g)=>s+g.clinical.length,0), color:"#6f42c1" },
                                                    { label:"Histology Files", value:Object.values(folderGroups||{}).reduce((s,g)=>s+g.histology.length,0), color:"#198754" },
                                                ].map((stat) => (
                                                    <div key={stat.label} style={{ flex:1, minWidth:"120px", background:"#fff", borderRadius:"8px", padding:"12px 16px", border:"1px solid #e0eeff" }}>
                                                        <div style={{ fontSize:"22px", fontWeight:700, color:stat.color }}>{stat.value}</div>
                                                        <div style={{ fontSize:"12px", color:"#888" }}>{stat.label}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div>
                                                <div style={{ fontSize:"12px", color:"#888", marginBottom:"6px", fontWeight:600 }}>DETECTED FOLDERS</div>
                                                <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                                                    {detectedFolderNames.slice(0,12).map((n) => (
                                                        <span key={n} style={{ padding:"3px 10px", borderRadius:"6px", background:"#fff", border:"1px solid #dde8f5", fontSize:"12px", fontFamily:"monospace", color:"#444" }}>{n}</span>
                                                    ))}
                                                    {detectedFolderNames.length>12 && <span style={{ padding:"3px 10px", borderRadius:"6px", background:"#f0f0f0", fontSize:"12px", color:"#888" }}>+{detectedFolderNames.length-12} more</span>}
                                                </div>
                                            </div>
                                            {totalFileCount>200 && (
                                                <div style={{ marginTop:"14px", padding:"10px 14px", borderRadius:"8px", background:"#fff8e1", border:"1px solid #ffe082", fontSize:"12px", color:"#7a5c00" }}>
                                                    <i className="mdi mdi-alert me-2" /><strong>Large batch ({totalFileCount} images).</strong> Do not close this tab until upload is complete.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <input ref={folderInputRef} type="file" style={{ display:"none" }} onChange={handleFolderSelect}
                                        // eslint-disable-next-line react/no-unknown-property
                                        webkitdirectory="" directory="" multiple />
                                </div>
                            </div>

                            {/* ── STEP 2: Preview & Upload ──────────────── */}
                            {showPreview && previewRows.length>0 && (
                                <div style={S.stepCard}>
                                    <StepHeader num={2} title={uploadComplete?"Upload Results":"Preview & Confirm"} active={currentStep===2} done={currentStep>2} />
                                    <div style={S.stepBody}>

                                        {/* Summary pills */}
                                        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"20px" }}>
                                            <span style={S.badge("#198754","#d1f0e0")}><i className="mdi mdi-check-circle" />{matchedRows.length} matched</span>
                                            {previewRows.length-matchedRows.length>0 && <span style={S.badge("#888","#f0f0f0")}><i className="mdi mdi-minus-circle" />{previewRows.length-matchedRows.length} unmatched (skipped)</span>}
                                            <span style={S.badge("#0d6efd","#e8f0fe")}><i className="mdi mdi-checkbox-marked" />{selectedCount} selected to upload</span>
                                            {limitWarnCount>0 && <span style={S.badge("#842029","#fde8ea")}><i className="mdi mdi-cancel" />{limitWarnCount} folder{limitWarnCount!==1?"s":""} disabled — limit exceeded</span>}
                                        </div>

                                        {/* Table */}
                                        <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #eee" }}>
                                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"13px" }}>
                                                <thead>
                                                    <tr style={{ background:"#f8f9fa" }}>
                                                        {/* Select-all checkbox */}
                                                        <th style={{ padding:"10px 14px", width:"40px", textAlign:"center", borderBottom:"2px solid #eee" }}>
                                                            {!uploadComplete && (
                                                                <input type="checkbox"
                                                                    checked={allSelected}
                                                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                                                    onChange={toggleSelectAll}
                                                                    disabled={isUploading}
                                                                    title={allSelected?"Deselect all":"Select all"}
                                                                    style={{ cursor:"pointer", width:"15px", height:"15px", accentColor:"var(--secondaryColor,#EA7F4A)" }}
                                                                />
                                                            )}
                                                        </th>
                                                        {["#","Folder Name","Matched Case","Histology","Clinical","Match",...(showStatusCol?["Status"]:[])].map((h) => (
                                                            <th key={h} style={{ padding:"10px 14px", fontWeight:600, fontSize:"11px", color:"#666", textTransform:"uppercase", letterSpacing:"0.5px", borderBottom:"2px solid #eee", whiteSpace:"nowrap", textAlign:["Histology","Clinical","Match","Status"].includes(h)?"center":"left" }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {previewRows.map((row, idx) => {
                                                        const st        = uploadStatus[row.folderName];
                                                        const isSelected= selectedFolders.has(row.folderName);
                                                        const rowBg     = row.limitExceeded ? "#fff5f5" : st==="done" ? "#f6fff9" : st==="error" ? "#fff8f8" : st==="partial" ? "#fffbf3" : !isSelected && row.matched ? "#fafafa" : row.matched ? "#fff" : "#fafafa";
                                                        const dimmed    = (row.matched && !isSelected && !uploadComplete) || row.limitExceeded;
                                                        return (
                                                            <tr key={row.folderName} style={{ background:rowBg, borderBottom:"1px solid #f0f0f0", opacity: dimmed?0.45:1, transition:"opacity 0.2s" }}>
                                                                {/* Checkbox — disabled for limit-exceeded rows */}
                                                                <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                                                    {row.matched && !uploadComplete && (
                                                                        <input type="checkbox"
                                                                            checked={isSelected && !row.limitExceeded}
                                                                            onChange={() => !row.limitExceeded && toggleFolder(row.folderName)}
                                                                            disabled={isUploading || row.limitExceeded}
                                                                            title={row.limitExceeded ? row.limitReason : undefined}
                                                                            style={{ cursor: row.limitExceeded ? "not-allowed" : "pointer", width:"15px", height:"15px", accentColor:"var(--secondaryColor,#EA7F4A)" }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td style={{ padding:"10px 14px", color:"#bbb", width:"36px" }}>{idx+1}</td>
                                                                <td style={{ padding:"10px 14px", fontWeight:600, fontFamily:"monospace", color:"#333" }}>{row.folderName}</td>
                                                                <td style={{ padding:"10px 14px" }}>
                                                                    {row.matched ? (
                                                                        <div>
                                                                            <span style={{ fontWeight:700, color:"#198754", fontSize:"13px" }}>{row.diagnosisid}</span>
                                                                            {row.diagnosis && <div style={{ fontSize:"11px", color:"#888", marginTop:"2px" }}>{row.diagnosis}</div>}
                                                                            {row.limitExceeded && (
                                                                                <div style={{ marginTop:"5px" }}>
                                                                                    <LimitExceededBadge reason={row.limitReason} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ color:"#ccc", fontSize:"12px", fontStyle:"italic" }}>No match found</span>
                                                                    )}
                                                                </td>
                                                                {/* Histology count */}
                                                                <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                                                    {row.histologyFiles.length>0
                                                                        ? <span style={{ fontWeight:700, color:"#0d6efd", background:"#e8f0fe", padding:"2px 8px", borderRadius:"10px", fontSize:"12px" }}>{row.histologyFiles.length}</span>
                                                                        : <span style={{ color:"#ddd" }}>—</span>}
                                                                </td>
                                                                {/* Clinical count */}
                                                                <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                                                    {row.clinicalFiles.length>0
                                                                        ? <span style={{ fontWeight:700, color:"#6f42c1", background:"#f3e8ff", padding:"2px 8px", borderRadius:"10px", fontSize:"12px" }}>{row.clinicalFiles.length}</span>
                                                                        : <span style={{ color:"#ddd" }}>—</span>}
                                                                </td>
                                                                <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                                                    {row.matched
                                                                        ? <i className="mdi mdi-check-circle" style={{ color:"#198754", fontSize:"18px" }} />
                                                                        : <i className="mdi mdi-minus-circle-outline" style={{ color:"#ddd", fontSize:"18px" }} />}
                                                                </td>
                                                                {showStatusCol && (
                                                                    <td style={{ padding:"10px 14px", textAlign:"center" }}><StatusBadge folderName={row.folderName} /></td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Limit note */}
                                        {limitWarnCount>0 && !uploadComplete && (
                                            <div style={{ marginTop:"14px", padding:"12px 16px", borderRadius:"8px", background:"#fde8ea", border:"1px solid #f5c6cb", fontSize:"12px", color:"#842029" }}>
                                                <i className="mdi mdi-cancel me-2" />
                                                <strong>{limitWarnCount} folder{limitWarnCount!==1?"s":""} disabled</strong> because the photo count exceeds the allowed limit
                                                (Clinical History max <strong>{CLINICAL_LIMIT}</strong>, Histology max <strong>{HISTOLOGY_LIMIT}</strong>).
                                                Please reduce the number of images in those folders and re-select.
                                            </div>
                                        )}

                                        {/* Upload button */}
                                        {!uploadComplete && selectedCount>0 && (
                                            <div style={{ marginTop:"20px", display:"flex", alignItems:"center", gap:"14px", flexWrap:"wrap" }}>
                                                <button className="btn btn-primarys create-quiz-btn" onClick={handleUpload} disabled={isUploading}>
                                                    {isUploading
                                                        ? <><span className="spinner-border spinner-border-sm me-2" />Uploading...</>
                                                        : <><i className="mdi mdi-upload me-2" />Start Upload ({selectedCount} folder{selectedCount!==1?"s":""})</>}
                                                </button>
                                                {previewRows.length-selectedCount>0 && (
                                                    <span style={{ fontSize:"12px", color:"#aaa" }}>
                                                        <i className="mdi mdi-information-outline me-1" />
                                                        {previewRows.length-selectedCount} folder{previewRows.length-selectedCount!==1?"s":""} will be skipped.
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {!uploadComplete && selectedCount===0 && matchedRows.length>0 && (
                                            <div style={{ marginTop:"16px", padding:"10px 16px", borderRadius:"8px", background:"#fff3cd", border:"1px solid #ffc107", fontSize:"13px", color:"#7a5c00" }}>
                                                <i className="mdi mdi-alert-outline me-2" />No folders selected. Check at least one folder to start the upload.
                                            </div>
                                        )}

                                        {/* Completion summary */}
                                        {uploadComplete && uploadSummary && (
                                            <div style={{ marginTop:"20px", borderRadius:"10px", padding:"18px 20px", background: uploadSummary.failed===0?"linear-gradient(135deg,#f6fff9 0%,#eafff2 100%)":"linear-gradient(135deg,#fff8f8 0%,#ffeaea 100%)", border:`1.5px solid ${uploadSummary.failed===0?"#b2e8c8":"#f5c6cb"}` }}>
                                                <div style={{ display:"flex", alignItems:"center", gap:"12px", flexWrap:"wrap" }}>
                                                    <i className={`mdi ${uploadSummary.failed===0?"mdi-check-circle":"mdi-alert-circle"}`} style={{ fontSize:"28px", color:uploadSummary.failed===0?"#198754":"#dc3545" }} />
                                                    <div>
                                                        <div style={{ fontWeight:700, fontSize:"15px", color:uploadSummary.failed===0?"#155724":"#721c24" }}>
                                                            Upload {uploadSummary.failed===0?"Completed Successfully":"Finished with Errors"}
                                                        </div>
                                                        <div style={{ fontSize:"12px", color:"#666", marginTop:"4px", display:"flex", gap:"14px", flexWrap:"wrap" }}>
                                                            <span><strong style={{ color:"#198754" }}>{uploadSummary.done}</strong> uploaded</span>
                                                            {uploadSummary.failed>0 && <span><strong style={{ color:"#dc3545" }}>{uploadSummary.failed}</strong> failed</span>}
                                                            {uploadSummary.skipped>0 && <span><strong style={{ color:"#888" }}>{uploadSummary.skipped}</strong> skipped</span>}
                                                        </div>
                                                    </div>
                                                    <button className="btn btn-primarys create-quiz-btn" onClick={resetAll} style={{ marginLeft:"auto" }}>
                                                        <i className="mdi mdi-refresh me-2" />Upload Another Batch
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
            <ToastContainer position="bottom-center" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover draggable />
        </>
    );
};

export default BulkImageUpload;
